import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { skipToken } from '@reduxjs/toolkit/query';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';

import { WeeklyPlannerDateRangeOptions as RangeOptions } from '~constants/maps';

import { dateInputFieldFormat, formatDateString, weeklyPlannerDayDisplayFormat } from '~libs/dayjs';
import { LoadingState } from '~libs/reduxUtils';
import { Appointment, Client, getHour, HeaderCellProps, Worker } from '~weekly-planner/lib/common';

import { InfiniteScroll, Spinner } from '~components';
import ContainerPanel from '~components/ContainerPanel';
import Icon, { faBriefcaseClock, faCalendar } from '~components/icon';
import FocusBar from '~weekly-planner/components/main/FocusBar';

import { useGetEventsQuery } from '~lookups/actions/events';

import { patchParams } from '~weekly-planner/reducers/weeklyPlanner';

import {
  selectDerivedDateRange,
  selectDerivedWeekRange,
  selectFocusedAppointment,
  selectParams,
} from '~weekly-planner/selectors';

import Row from './Row';

interface ComponentProps {
  headerCell: React.FC<HeaderCellProps>;
  rows: Array<Worker | Client>;
  focusId?: number | null;
  loadingState: LoadingState | null;
  getAppointmentTitle: (appointment: Appointment) => string;
}

const View: React.FC<ComponentProps> = ({
  headerCell: HeaderCell,
  rows,
  focusId,
  loadingState,
  getAppointmentTitle,
}) => {
  const [isMousedown, setIsMousedown] = useState<boolean>(false);
  const [startX, setStartX] = useState(0);
  const [initialScrollLeft, setInitialScrollLeft] = useState(0);

  const dispatch = useAppDispatch();
  const { date, limit } = useAppSelector(selectParams);
  const { week_start } = useAppSelector(selectDerivedDateRange);
  const isLoading = loadingState === 'pending';
  const isLoaded = loadingState === 'fulfilled';

  // most recent Monday
  const weekStart = dayjs(week_start).weekday(0);
  const daysOfWeek = Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day'));

  const focused = useAppSelector(selectFocusedAppointment);
  const { appointment: focusedAppointment } = focused || {};
  const { start_time = '' } = focusedAppointment || {};
  const focusedRow = rows?.find(({ id }) => Number(id) === focusId);

  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const scrollableContainerRef = useRef<HTMLDivElement>(null);

  const weekRange = useAppSelector(selectDerivedWeekRange);
  const { data: events } = useGetEventsQuery(weekRange ?? skipToken);

  const className = classNames('daily-view-container', {
    loading: isLoading,
  });

  const wrapperClassName = classNames('daily-view-wrapper', {
    dragging: isMousedown,
    loading: isLoading,
  });
  // Only collapse if there's no focused appointment
  const isCollapsed = !focusedAppointment;

  const handleMouseMove = (event: any) => {
    if (isMousedown && contentWrapperRef.current) {
      // Calculate the horizontal movement
      const deltaX = startX - event.clientX;

      const newScrollPosition = initialScrollLeft + deltaX;
      // Update scroll position
      contentWrapperRef.current.scrollLeft = newScrollPosition;
    }
  };

  // Handle the mouse down event to start dragging
  const handleMouseDown = (event: React.MouseEvent) => {
    if (contentWrapperRef) {
      document.addEventListener('mouseup', handleMouseUp);
      setIsMousedown(true);
      setStartX(event.clientX); // Track initial mouse position
      setInitialScrollLeft(contentWrapperRef?.current?.scrollLeft ?? 0); // Track initial scroll position
    }
  };

  // Handle the mouse up or mouse leave event to stop dragging
  const handleMouseUp = () => {
    setIsMousedown(false);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  const focusHour = (hour: number) => {
    const hourElement = document.getElementById(`hour-${hour}`);
    if (hourElement) {
      hourElement.scrollIntoView({
        behavior: 'auto',
        block: 'center',
        inline: 'center',
      });
    }
  };

  useEffect(() => {
    // On mount, scroll to middle of the timeline
    focusHour(12);
  }, []);

  useEffect(() => {
    // On focus change, scroll to expected time of appointment
    focusHour(getHour(start_time ?? ''));
  }, [focusedAppointment]);

  const setView = (selectedDate: Dayjs) => {
    const formattedDate = selectedDate.format(dateInputFieldFormat);
    dispatch(
      patchParams({
        date: formattedDate,
        rangeType: RangeOptions.DAILY,
      }),
    );
  };

  const renderHeader = (days: Dayjs[]) => (
    <>
      <div className="header-cell title" title="View:">
        View:
      </div>
      {days.map((day) => {
        const formattedDate = day.format(weeklyPlannerDayDisplayFormat);
        const publicHoliday = events?.find(({ start_date }) => formatDateString(start_date) === formatDateString(day));
        const className = classNames('header-cell', {
          selected: day.isSame(date, 'day'),
          'public-holiday': !!publicHoliday,
        });

        return (
          <div key={day.toString()} className={className}>
            <button className="clickable btn btn-link p-0" onClick={() => setView(day)} title={formattedDate}>
              {publicHoliday && <Icon icon={faCalendar} className="me-1" title={publicHoliday.name} />}
              {formattedDate}
            </button>
          </div>
        );
      })}
    </>
  );

  const renderHours = () => (
    <>
      {[...Array(24)].map((_, index) => {
        const id = `hour-${index}`;
        return (
          <div key={id} id={id} className="hour-marker">
            {`${index}:00`}
          </div>
        );
      })}
    </>
  );

  const renderTimeline = () => {
    const className = classNames('timeline-wrapper', {
      dragging: isMousedown,
    });

    return (
      <div className={className} onMouseDown={handleMouseDown} role="grid" tabIndex={0} aria-label="Timeline">
        <div className="timeline-header">Timeline</div>
        <div className="timeline-content">{renderHours()}</div>
      </div>
    );
  };

  const renderFocusedRow = (row: Worker | Client, getAppointmentTitle: (appointment: Appointment) => string) => (
    <div className="focus-row">
      <Row
        key={`${row.id}-0`}
        headerCell={<HeaderCell data={row} date={dayjs(week_start)} />}
        getAppointmentTitle={getAppointmentTitle}
        data={row}
        date={dayjs(date)}
        events={row.events[date]}
      />
    </div>
  );

  const renderRows = (
    rows: Array<Worker | Client>,
    getAppointmentTitle: (appointment: Appointment) => string,
    focusedRow?: Worker | Client,
  ) =>
    rows
      ?.filter(({ id }) => focusedRow?.id !== id)
      .map((row, index) => {
        let gridRow = index + 1;
        if (!focusedRow) gridRow += 1;
        const style: CSSProperties = {
          gridRow,
        };
        return (
          <Row
            key={`${row.id}-${index}`}
            headerCell={<HeaderCell data={row} date={dayjs(week_start)} style={style} />}
            data={row}
            date={dayjs(date)}
            getAppointmentTitle={getAppointmentTitle}
            events={row.events[date]}
            style={style}
          />
        );
      });

  const loader = <Spinner loading={isLoading} className="p-5" />;
  const emptyRows = isLoaded && rows.length === 0;

  const loadMore = () => {
    if (!isLoading && limit === rows.length) {
      dispatch(patchParams({ limit: limit + 30 }));
    }
  };

  return (
    <ContainerPanel
      className={wrapperClassName}
      isCollapsed={isCollapsed}
      canCollapse={false}
      canResize={false}
      sidePanel={<FocusBar />}
    >
      <div
        ref={scrollableContainerRef}
        className={className}
        onMouseMove={handleMouseMove}
        role="grid"
        tabIndex={0}
        aria-label="Daily View Grid"
      >
        <div className="header-wrapper">{renderHeader(daysOfWeek)}</div>
        {!emptyRows ? (
          <InfiniteScroll
            dataLength={rows.length}
            ref={contentWrapperRef}
            loadMore={loadMore}
            className="content-wrapper"
            hasMore={true}
            loader={loader}
            triggerOffset="500px"
            endMessage={
              <div className="empty-results">
                <Icon icon={faBriefcaseClock} />
                <p>End of results - Please adjust filters to find more.</p>
              </div>
            }
          >
            {renderTimeline()}
            {focusedRow && renderFocusedRow(focusedRow, getAppointmentTitle)}
            {renderRows(rows, getAppointmentTitle, focusedRow)}
          </InfiniteScroll>
        ) : (
          <div className="empty-results">
            <Icon size="2x" className="mb-3" icon={faBriefcaseClock} />
            <p>No Results Found - Please adjust filters to find more.</p>
          </div>
        )}
      </div>
    </ContainerPanel>
  );
};

export default View;
